import { useMemo, useState } from "react";
import { dateLabel, money } from "../../../lib/finance";
import { Private } from "../../../components/Privacy";

export interface HeatmapDay {
  day: number;
  spending: number;
}

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

// Levels 1-4 split the days that had any spending into quartiles, rather than scaling against the
// peak, so one large purchase does not wash every other day out to the palest shade.
function levelThresholds(days: HeatmapDay[]) {
  const sorted = days.filter((d) => d.spending > 0).map((d) => d.spending).sort((a, b) => a - b);
  const at = (q: number) => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))];
  return sorted.length ? [at(0.25), at(0.5), at(0.75)] : [];
}

export default function DailySpendingHeatmap({
  days,
  month,
  daysInMonth,
  baseCurrency,
}: {
  days: HeatmapDay[];
  month: string;
  daysInMonth: number;
  baseCurrency: string;
}) {
  const [picked, setPicked] = useState<number | null>(null);
  const bySpend = useMemo(() => new Map(days.map((d) => [d.day, d.spending])), [days]);
  const thresholds = useMemo(() => levelThresholds(days), [days]);
  const levelOf = (spending: number) =>
    spending <= 0 ? 0 : 1 + thresholds.filter((t) => spending > t).length;
  const [year, monthNumber] = month.split("-").map(Number);
  const leading = new Date(year, monthNumber - 1, 1).getDay();
  const selected = picked !== null && bySpend.has(picked) ? picked : null;

  return (
    <div className="spending-heatmap">
      <div className="spending-heatmap-grid" role="grid" aria-label="Spending by day">
        {WEEKDAYS.map((w, i) => (
          <span key={i} className="spending-heatmap-weekday" aria-hidden="true">{w}</span>
        ))}
        {Array.from({ length: leading }, (_, i) => <span key={`pad-${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const spending = bySpend.get(day);
          if (spending === undefined)
            return <span key={day} className="spending-heatmap-cell is-future" aria-hidden="true">{day}</span>;
          return (
            <button
              key={day}
              type="button"
              className="spending-heatmap-cell"
              data-level={levelOf(spending)}
              aria-pressed={selected === day}
              aria-label={`${dateLabel(`${month}-${String(day).padStart(2, "0")}`)}, ${spending > 0 ? "spent" : "no spending"}`}
              onClick={() => setPicked(selected === day ? null : day)}
            >
              {day}
            </button>
          );
        })}
      </div>
      <div className="spending-heatmap-footer">
        <p className="spending-heatmap-readout" aria-live="polite">
          {selected === null ? (
            "Tap a day for its total"
          ) : (
            <>
              {dateLabel(`${month}-${String(selected).padStart(2, "0")}`)} ·{" "}
              <strong><Private>{money(bySpend.get(selected) || 0, baseCurrency)}</Private></strong>
            </>
          )}
        </p>
        <div className="spending-heatmap-legend" aria-hidden="true">
          <span>Less</span>
          {[0, 1, 2, 3, 4].map((level) => <i key={level} data-level={level} />)}
          <span>More</span>
        </div>
      </div>
    </div>
  );
}
